import { useEffect, useState } from 'react';

import { API_BASE_URL } from '../config/config';
import { CourseClient, CourseMinifiedDTO } from '../services/apiClient';
import axiosInstance from '../services/axiosInstance';

const CACHE_KEY = 'exploreCourses:v2';

// The v1 cache held { etag, courses } and is never read again. It has to be actively removed
// rather than just abandoned: the serialized dataset is several MB against a ~5 MB localStorage
// quota, so a stale v1 entry would permanently deny the v2 write any room.
const LEGACY_CACHE_KEY = 'exploreCourses:v1';

const courseClient = new CourseClient(API_BASE_URL, axiosInstance);

interface CachedPayload {
  version: string;
  courses: CourseMinifiedDTO[];
}

interface ExploreCoursesState {
  courses: CourseMinifiedDTO[];
  isLoading: boolean;
  error: string | null;
}

const readCache = (): CachedPayload | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as CachedPayload) : null;
  } catch {
    return null;
  }
};

const writeCache = (payload: CachedPayload): void => {
  try {
    localStorage.removeItem(LEGACY_CACHE_KEY);
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Storage full/unavailable (private browsing, quota) — fine to just skip caching.
    // Worth knowing this is the normal outcome today, not the exception: the serialized
    // dataset is several MB and localStorage caps out around 5 MB in most browsers, so
    // the write almost always throws here and the version check never gets a cache to
    // compare against. Moving this to IndexedDB (no practical size cap) is the real fix.
  }
};

// The snapshot blob holds the bare CourseMinifiedDTO array — not an ApiResponse envelope, which
// is what the /courses/explore endpoint returns. Don't reach for `.data` on this one.
const downloadSnapshot = async (
  url: string,
  signal: AbortSignal,
): Promise<CourseMinifiedDTO[]> => {
  // Deliberately plain fetch, not axiosInstance: this is an absolute storage/CDN URL, so it must
  // not pick up the API base URL, the bearer token, or the 401-redirect interceptor. The blob is
  // stored with Content-Encoding: br and the browser decompresses it transparently.
  const response = await fetch(url, { signal });

  if (!response.ok) {
    throw new Error(
      `Explore snapshot download failed with ${response.status}.`,
    );
  }

  return (await response.json()) as CourseMinifiedDTO[];
};

// The pre-snapshot path. Still live and still correct — it just rebuilds the whole dataset per
// request instead of serving a pre-built file.
const downloadFromApi = async (): Promise<CourseMinifiedDTO[]> => {
  const response = await axiosInstance.get('/courses/explore');
  // axiosInstance is configured with transformResponse: [] (the generated NSwag clients do their
  // own JSON.parse) — response.data here is a raw JSON string, not a parsed object.
  const body =
    typeof response.data === 'string'
      ? JSON.parse(response.data)
      : response.data;

  return body?.data || [];
};

// Public Explore map data source. Loads the full active-course dataset once (tens of thousands of
// rows, but a lean projection — see CourseMinifiedDTO) from a snapshot published to blob storage.
//
// Two hops: ask the API which snapshot is current, then download that snapshot straight from
// storage. The dataset never passes through the Function, so no visitor pays for the rebuild. The
// manifest's version doubles as the cache key — matching version means the cached copy is current
// and nothing is downloaded at all.
export const useExploreCourses = (): ExploreCoursesState => {
  const [courses, setCourses] = useState<CourseMinifiedDTO[]>(
    () => readCache()?.courses ?? [],
  );
  const [isLoading, setIsLoading] = useState(courses.length === 0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const loadCourses = async (): Promise<void> => {
      const cached = readCache();

      try {
        const manifest = (await courseClient.exploreManifest()).data;

        if (cancelled) {
          return;
        }

        if (!manifest?.url) {
          throw new Error('Explore manifest returned no dataset URL.');
        }

        if (cached && manifest.version && cached.version === manifest.version) {
          setCourses(cached.courses);
          return;
        }

        const data = await downloadSnapshot(manifest.url, controller.signal);

        if (cancelled) {
          return;
        }

        setCourses(data);

        if (manifest.version) {
          writeCache({ version: manifest.version, courses: data });
        }
      } catch (snapshotError) {
        if (cancelled) {
          return;
        }

        // The manifest 404s until the first snapshot is published, and a storage outage or a
        // missing CORS rule shouldn't take the map down either. The original endpoint builds the
        // same dataset on demand, so try that before showing the user an error.
        console.warn(
          'Explore snapshot unavailable, falling back to the API:',
          snapshotError,
        );

        try {
          const data = await downloadFromApi();

          if (!cancelled) {
            setCourses(data);
          }
        } catch (err) {
          console.error('Error fetching explore courses:', err);

          if (!cancelled) {
            // If we at least have a stale cached copy, prefer showing that over nothing.
            if (cached) {
              setCourses(cached.courses);
            } else {
              setError(
                'Unable to load courses right now. Please try again later.',
              );
            }
          }
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadCourses();

    return (): void => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  return { courses, isLoading, error };
};
